import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Filter, FileUp, Paperclip, Upload } from 'lucide-react';
import PageWrapper from '@/components/layout/PageWrapper';
import DataTable, { type ColumnDef } from '@/components/shared/DataTable';
import StatusBadge from '@/components/shared/StatusBadge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import POForm from '@/components/orderedParts/POForm';
import { Button } from '@/components/ui/button';
import { orderedPartsApi, vendorsApi } from '@/api';
import { useAuth } from '@/auth/AuthContext';
import { formatDate, cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import type { OrderedPart, PartAttachment } from '@/types';

function formatCents(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

export default function OrderedParts() {
  const { currentUser, isImpersonating, hasRole } = useAuth();
  const queryClient = useQueryClient();
  const isExtVendor = currentUser?.role === 'ext_vendor';
  const canEdit = hasRole(['repair_tech', 'pm_admin', 'super_admin']);

  const [selectedPart, setSelectedPart] = useState<OrderedPart | null>(null);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const receiptInputRef = useRef<HTMLInputElement>(null);

  const { data: receiptAttachments = [], refetch: refetchAttachments } = useQuery<PartAttachment[]>({
    queryKey: ['orderedParts', 'attachments', selectedPart?.id],
    queryFn: () => orderedPartsApi.listAttachments(selectedPart!.id),
    enabled: !!selectedPart,
  });

  async function handleReceiptUpload() {
    if (!selectedPart || !receiptFile) return;
    setUploading(true);
    try {
      const contentType = receiptFile.type || 'application/octet-stream';
      const { uploadUrl, s3Key } = await orderedPartsApi.presignAttachment(
        selectedPart.id,
        receiptFile.name,
        contentType,
      );
      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: receiptFile,
      });
      await orderedPartsApi.recordAttachment(selectedPart.id, s3Key, receiptFile.name);
      await refetchAttachments();
      queryClient.invalidateQueries({ queryKey: ['orderedParts'] });
      toast({ title: 'Receipt uploaded' });
      setReceiptFile(null);
    } catch {
      toast({ title: 'Failed to upload receipt', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  }
  // When impersonating ext_vendor with no assigned vendor, let them pick one to preview
  const needsVendorPicker = isExtVendor && isImpersonating && currentUser?.vendorId == null;

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [vendorFilter, setVendorFilter] = useState<string>('all');

  // For real ext_vendor users, lock to their assigned vendor.
  // For impersonation with no vendorId, use the picker.
  const effectiveVendorFilter = isExtVendor && !needsVendorPicker
    ? currentUser?.vendorId != null
      ? String(currentUser.vendorId)
      : 'none'
    : vendorFilter;

  const { data, isLoading } = useQuery({
    queryKey: ['orderedParts', statusFilter, effectiveVendorFilter],
    queryFn: () =>
      orderedPartsApi.list({
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        ...(effectiveVendorFilter !== 'all' && effectiveVendorFilter !== 'none'
          ? { vendor_id: effectiveVendorFilter }
          : {}),
      }),
  });

  const { data: vendors } = useQuery({
    queryKey: ['vendors'],
    queryFn: () => vendorsApi.list(),
    enabled: !isExtVendor || needsVendorPicker,
  });

  const columns: ColumnDef<OrderedPart>[] = [
    {
      key: 'poNumber',
      header: 'PO #',
      sortable: true,
      cell: (row) => (
        <span className="font-mono text-xs font-medium">{row.poNumber}</span>
      ),
    },
    {
      key: 'title',
      header: 'Title',
      cell: (row) => <span className="font-medium">{row.title}</span>,
    },
    {
      key: 'unitNumber',
      header: 'Unit #',
      cell: (row) => (
        <span className="font-mono text-xs">{row.unitNumber ?? '—'}</span>
      ),
    },
    {
      key: 'vendorId',
      header: 'Vendor',
      cell: (row) => (
        <span className="text-muted-foreground">
          {vendors?.find((v) => v.id === row.vendorId)?.name ?? `Vendor #${row.vendorId}`}
        </span>
      ),
    },
    {
      key: 'company',
      header: 'Company',
      cell: (row) => (
        <span className="text-muted-foreground">{row.company ?? '—'}</span>
      ),
    },
    {
      key: 'quotedPrice',
      header: 'Quoted',
      sortable: true,
      cell: (row) => formatCents(row.quotedPrice),
    },
    {
      key: 'actualPrice',
      header: 'Actual',
      cell: (row) => formatCents(row.actualPrice),
    },
    {
      key: 'paymentMethod',
      header: 'Payment',
      cell: (row) => (
        <span className="text-sm text-muted-foreground">
          {row.paymentMethod}
          {row.cardLastFour && ` (••${row.cardLastFour})`}
        </span>
      ),
    },
    {
      key: 'poStatus',
      header: 'Status',
      cell: (row) => (
        <div className="flex items-center gap-2">
          <StatusBadge status={row.poStatus} />
          {Number(row.attachmentCount ?? 0) === 0 && (
            <span className="flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400">
              <AlertCircle className="h-3 w-3" />
              No receipt
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'returned',
      header: 'Returned',
      cell: (row) =>
        row.returned ? (
          <span className="text-yellow-600 dark:text-yellow-400 text-sm font-medium">
            Yes
          </span>
        ) : (
          <span className="text-muted-foreground text-sm">No</span>
        ),
    },
    {
      key: 'repairDate',
      header: 'Date',
      sortable: true,
      cell: (row) => (
        <span className="text-muted-foreground">{formatDate(row.repairDate)}</span>
      ),
    },
  ];

  return (
    <PageWrapper
      title={isExtVendor ? 'My Orders' : 'Ordered Parts'}
      subtitle={isExtVendor ? 'Parts and orders for your company' : 'Purchase orders and parts tracking'}
    >
      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Filter className="h-4 w-4 text-muted-foreground" />

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="New">New</SelectItem>
            <SelectItem value="In-Progress">In Progress</SelectItem>
            <SelectItem value="Parts Ordered">Parts Ordered</SelectItem>
            <SelectItem value="Done">Done</SelectItem>
          </SelectContent>
        </Select>

        {(!isExtVendor || needsVendorPicker) && (
          <Select value={vendorFilter} onValueChange={setVendorFilter}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder={needsVendorPicker ? 'Preview as vendor…' : 'Vendor'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Vendors</SelectItem>
              {vendors?.map((v) => (
                <SelectItem key={v.id} value={String(v.id)}>
                  {v.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {data && (
          <span className="text-sm text-muted-foreground">
            {data.total} result{data.total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <DataTable<OrderedPart>
        columns={columns}
        data={data?.items ?? []}
        isLoading={isLoading}
        emptyMessage="No ordered parts found."
        keyExtractor={(row) => row.id}
        onRowClick={(row) => setSelectedPart(row)}
        rowClassName={(row) => {
          if (row.poStatus === 'Done' && Number(row.attachmentCount ?? 0) === 0)
            return 'bg-red-50/70 dark:bg-red-900/20 cursor-pointer';
          if (row.poStatus === 'Done')
            return 'bg-green-50/60 dark:bg-green-500/10 cursor-pointer';
          if (Number(row.attachmentCount ?? 0) === 0)
            return 'bg-yellow-50/60 dark:bg-yellow-500/10 cursor-pointer';
          return 'cursor-pointer';
        }}
      />

      {/* Row detail / edit dialog */}
      <Dialog open={!!selectedPart} onOpenChange={(open) => { if (!open) { setSelectedPart(null); setReceiptFile(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {canEdit ? 'Edit Order' : 'Order Details'} — PO #{selectedPart?.poNumber}
            </DialogTitle>
          </DialogHeader>

          {selectedPart && canEdit && (
            <POForm
              existingPart={selectedPart}
              onSuccess={() => setSelectedPart(null)}
              onCancel={() => setSelectedPart(null)}
            />
          )}

          {selectedPart && !canEdit && (
            <div className="space-y-3 text-sm">
              {[
                { label: 'Part Ordered', value: selectedPart.title },
                { label: 'Description', value: selectedPart.description ?? '—' },
                { label: 'Vendor', value: vendors?.find((v) => v.id === selectedPart.vendorId)?.name ?? `Vendor #${selectedPart.vendorId}` },
                { label: 'Company', value: selectedPart.company ?? '—' },
                { label: 'Quoted Price', value: formatCents(selectedPart.quotedPrice) },
                { label: 'Actual Price', value: formatCents(selectedPart.actualPrice) },
                { label: 'Payment', value: selectedPart.paymentMethod + (selectedPart.cardLastFour ? ` (••${selectedPart.cardLastFour})` : '') },
                { label: 'Date', value: formatDate(selectedPart.repairDate) },
                { label: 'Status', value: selectedPart.poStatus },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between gap-4 border-b border-border pb-2 last:border-0">
                  <span className="text-muted-foreground">{label}</span>
                  <span className="font-medium text-right">{value}</span>
                </div>
              ))}
            </div>
          )}

          {/* Receipt upload — visible to all roles */}
          {selectedPart && (
            <div className="space-y-3 border-t border-border pt-4">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Receipts</p>

              {receiptAttachments.length > 0 && (
                <ul className="divide-y divide-border rounded-md border border-border">
                  {receiptAttachments.map((att) => (
                    <li key={att.id} className="flex items-center justify-between px-3 py-2">
                      <span className="flex items-center gap-2 text-sm truncate">
                        <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span className="truncate">{att.fileName}</span>
                      </span>
                      <a
                        href={att.viewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-3 shrink-0 text-xs font-medium text-primary hover:underline"
                      >
                        View
                      </a>
                    </li>
                  ))}
                </ul>
              )}

              <input
                ref={receiptInputRef}
                type="file"
                accept="image/*,.pdf"
                className="hidden"
                onChange={(e) => setReceiptFile(e.target.files?.[0] ?? null)}
              />
              <div
                className={cn(
                  'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-5 text-center transition-colors',
                  receiptFile
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
                )}
                onClick={() => receiptInputRef.current?.click()}
              >
                <Upload className="h-5 w-5 text-muted-foreground" />
                {receiptFile ? (
                  <p className="text-sm font-medium">{receiptFile.name}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">Click to select image or PDF</p>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1.5"
                  onClick={() => receiptInputRef.current?.click()}
                >
                  <FileUp className="h-3.5 w-3.5" />
                  Browse
                </Button>
                <Button
                  size="sm"
                  onClick={handleReceiptUpload}
                  disabled={!receiptFile || uploading}
                >
                  {uploading ? 'Uploading…' : 'Upload Receipt'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageWrapper>
  );
}
